import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import './apiDocumentation.css';

// Code block with copy functionality
const CodeBlock: React.FC<{ code: string; label?: string }> = ({
  code,
  label,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="api-docs-code-block">
      <div className="api-docs-code-header">
        <span className="api-docs-code-label">{label || 'Code'}</span>
        <button
          className={`api-docs-copy-btn ${copied ? 'copied' : ''}`}
          onClick={handleCopy}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <div className="api-docs-code-content">
        <pre>{code}</pre>
      </div>
    </div>
  );
};

// Tabbed code examples
const CodeTabs: React.FC<{
  tabs: { label: string; code: string; language: string }[];
}> = ({ tabs }) => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div className="api-docs-tabs">
      <div className="api-docs-tab-list">
        {tabs.map((tab, index) => (
          <button
            key={tab.label}
            className={`api-docs-tab ${index === activeTab ? 'active' : ''}`}
            onClick={() => setActiveTab(index)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {tabs.map((tab, index) => (
        <div
          key={tab.label}
          className={`api-docs-tab-content ${index === activeTab ? 'active' : ''}`}
        >
          <CodeBlock code={tab.code} label={tab.language} />
        </div>
      ))}
    </div>
  );
};

const ApiDocumentationPage: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavigationAttempt = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    }
  };

  // Code examples for the tabs
  const codeExamples = [
    {
      label: 'React Native',
      language: 'JavaScript',
      code: `async function exportToTradeMagic() {
  try {
    // 1. Get user's collection
    const collection = await getUserCollection();

    // 2. Generate CSV
    const csvData = generateCSV(collection);

    // 3. Encode to base64
    const base64Data = btoa(unescape(encodeURIComponent(csvData)));

    // 4. Create session
    const response = await fetch(
      'https://api.trademagic.gg/api/v1/import-session',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          csvData: base64Data,
          source: 'your-source-id'
        })
      }
    );

    if (!response.ok) {
      throw new Error('Failed to create import session');
    }

    const { importUrl, cardCount, skippedRows } = await response.json();

    // 5. Show confirmation to user
    Alert.alert(
      'Export Ready',
      \`\${cardCount} cards ready to import\${skippedRows > 0 ? \` (\${skippedRows} skipped)\` : ''}\`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Open TradeMagic', onPress: () => Linking.openURL(importUrl) }
      ]
    );
  } catch (error) {
    Alert.alert('Export Failed', error.message);
  }
}`,
    },
    {
      label: 'Python',
      language: 'Python',
      code: `import base64
import requests
import webbrowser

def export_to_trademagic(cards):
    # Build CSV
    csv_lines = ["export_type;scryfall_uuid;set_code;quantity;foil_quantity;card_name"]
    for card in cards:
        csv_lines.append(
            f"card;{card['scryfall_uuid']};{card['set_code']};"
            f"{card['quantity']};{card['foil_quantity']};{card['name']}"
        )

    csv_data = "\\n".join(csv_lines)
    csv_base64 = base64.b64encode(csv_data.encode()).decode()

    # Create session
    response = requests.post(
        "https://api.trademagic.gg/api/v1/import-session",
        json={
            "csvData": csv_base64,
            "source": "your-source-id"
        }
    )

    if response.status_code == 200:
        result = response.json()
        print(f"Session created: {result['cardCount']} cards")
        print(f"Skipped: {result['skippedRows']} rows")

        # Open browser for user to complete import
        webbrowser.open(result['importUrl'])
        return result
    else:
        print(f"Error: {response.text}")
        return None`,
    },
    {
      label: 'Node.js',
      language: 'JavaScript',
      code: `const axios = require('axios');

async function exportToTradeMagic(cards) {
  // Build CSV
  const csvLines = ['export_type;scryfall_uuid;set_code;quantity;foil_quantity;card_name'];

  for (const card of cards) {
    csvLines.push(
      \`card;\${card.scryfallUuid};\${card.setCode};\${card.quantity};\${card.foilQuantity};\${card.name}\`
    );
  }

  const csvData = csvLines.join('\\n');
  const csvBase64 = Buffer.from(csvData).toString('base64');

  // Create session
  const response = await axios.post(
    'https://api.trademagic.gg/api/v1/import-session',
    {
      csvData: csvBase64,
      source: 'your-source-id'
    }
  );

  console.log(\`Session created: \${response.data.cardCount} cards\`);
  console.log(\`Import URL: \${response.data.importUrl}\`);

  return response.data;
}`,
    },
  ];

  return (
    <>
      <SEOHelmet
        title="API Documentation - TradeMagic"
        description="Developer documentation for integrating with TradeMagic's import API. Learn how to export collections from your app to TradeMagic."
        canonical="https://trademagic.gg/docs/api"
        type="website"
      />

      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          {
            name: 'API Documentation',
            url: 'https://trademagic.gg/docs/api',
          },
        ]}
      />

      <div className="api-docs-layout">
        {isMobile && isMobileMenuOpen && (
          <div
            className="api-docs-mobile-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="api-docs-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/docs/api', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />

          <div className="api-docs-main">
            <TopNavigationBar
              title="API Documentation"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />

            <div className="api-docs-container">
              {/* Header */}
              <header className="api-docs-header">
                <h1 className="api-docs-title">TradeMagic Import API</h1>
                <p className="api-docs-subtitle">
                  Integration guide for partners
                </p>
              </header>

              {/* Overview */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Overview</h2>
                <p className="api-docs-text">
                  TradeMagic supports a session-based import flow that handles
                  large collections without URL length limitations. Instead of
                  encoding CSV data in the URL, you POST the data to our
                  backend, receive a session ID, and redirect users to complete
                  the import.
                </p>

                <div className="api-docs-diagram">
                  <pre>
                    {`┌─────────────────┐     POST /import-session      ┌─────────────────┐
│    Your App     │ ──────────────────────────►   │ TradeMagic API  │
│                 │   { csvData, source }         │                 │
│                 │ ◄──────────────────────────   │                 │
│                 │   { sessionId, importUrl }    │                 │
└────────┬────────┘                               └─────────────────┘
         │
         │  Open browser to importUrl
         ▼
┌─────────────────┐
│ TradeMagic Web  │  User selects condition → previews → logs in → imports
└─────────────────┘`}
                  </pre>
                </div>
              </section>

              {/* API Endpoint */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">API Endpoint</h2>
                <div className="api-docs-endpoint">
                  <span className="api-docs-method-badge">POST</span>
                  <span className="api-docs-endpoint-url">
                    https://api.trademagic.gg/api/v1/import-session
                  </span>
                  <p className="api-docs-endpoint-note">
                    Content-Type: application/json | No authentication required
                  </p>
                </div>
              </section>

              {/* Request */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Request</h2>
                <CodeBlock
                  label="JSON"
                  code={`{
  "csvData": "<base64-encoded-csv-string>",
  "source": "your-source-id"
}`}
                />

                <div className="api-docs-table-wrapper">
                  <table className="api-docs-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Type</th>
                        <th>Required</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <code>csvData</code>
                        </td>
                        <td>string</td>
                        <td>
                          <span className="api-docs-required">Yes</span>
                        </td>
                        <td>Base64-encoded CSV data (UTF-8)</td>
                      </tr>
                      <tr>
                        <td>
                          <code>source</code>
                        </td>
                        <td>string</td>
                        <td>
                          <span className="api-docs-required">Yes</span>
                        </td>
                        <td>Partner identifier (provided by TradeMagic during onboarding)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* CSV Format */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">CSV Format</h2>
                <p className="api-docs-text">
                  Generate a <strong>semicolon-delimited</strong> CSV with the
                  following structure:
                </p>

                <CodeBlock
                  label="CSV"
                  code={`export_type;scryfall_uuid;set_code;quantity;foil_quantity;card_name;set_name;cardMarketId;english_card_name;lang;collector_number
card;40c92203-17df-4f10-92c6-ebcc79f01357;dft;1;0;Lifecraft Engine;Aetherdrift;806111;Lifecraft Engine;EN;234
card;f712ac26-dca4-459b-84c1-010597007f60;aer;1;0;Metallic Rebuke;Aether Revolt;294830;Metallic Rebuke;EN;39`}
                />

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">Required Columns</h3>
                  <div className="api-docs-table-wrapper">
                    <table className="api-docs-table">
                      <thead>
                        <tr>
                          <th>Column</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <code>export_type</code>
                          </td>
                          <td>
                            Must be <code>card</code> for card entries (other
                            types are skipped)
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <code>scryfall_uuid</code>
                          </td>
                          <td>
                            Scryfall UUID (primary identifier, ensures 100%
                            match rate)
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <code>quantity</code>
                          </td>
                          <td>Number of non-foil copies</td>
                        </tr>
                        <tr>
                          <td>
                            <code>foil_quantity</code>
                          </td>
                          <td>Number of foil copies</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">Optional Columns</h3>
                  <div className="api-docs-table-wrapper">
                    <table className="api-docs-table">
                      <thead>
                        <tr>
                          <th>Column</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <code>card_name</code>
                          </td>
                          <td>Card name (used for error reporting)</td>
                        </tr>
                        <tr>
                          <td>
                            <code>set_code</code>
                          </td>
                          <td>Set code (used for error reporting)</td>
                        </tr>
                        <tr>
                          <td>
                            <code>english_card_name</code>
                          </td>
                          <td>English name (preferred over card_name)</td>
                        </tr>
                        <tr>
                          <td>
                            <code>lang</code>
                          </td>
                          <td>Language code (default: "en")</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <p className="api-docs-text">
                  <strong>Important:</strong> Rows with{' '}
                  <code>export_type=deck</code> are automatically skipped. Rows
                  with <code>quantity=0</code> AND <code>foil_quantity=0</code>{' '}
                  are also skipped.
                </p>
              </section>

              {/* Response */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Response</h2>
                <CodeBlock
                  label="JSON (200 OK)"
                  code={`{
  "sessionId": "c90d922d-0221-4df6-a185-a59056bba894",
  "cardCount": 15234,
  "skippedRows": 42,
  "parseWarnings": ["Row 5: Invalid UUID format"],
  "expiresAt": "2026-01-12T00:26:10.504Z",
  "importUrl": "https://trademagic.gg/import/your-source-id?session=c90d922d-0221-4df6-a185-a59056bba894"
}`}
                />

                <div className="api-docs-table-wrapper">
                  <table className="api-docs-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Type</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <code>sessionId</code>
                        </td>
                        <td>string</td>
                        <td>Unique session identifier</td>
                      </tr>
                      <tr>
                        <td>
                          <code>cardCount</code>
                        </td>
                        <td>number</td>
                        <td>Number of valid cards parsed</td>
                      </tr>
                      <tr>
                        <td>
                          <code>skippedRows</code>
                        </td>
                        <td>number</td>
                        <td>Rows skipped (deck entries, invalid data)</td>
                      </tr>
                      <tr>
                        <td>
                          <code>parseWarnings</code>
                        </td>
                        <td>string[]</td>
                        <td>Specific parsing issues (for debugging)</td>
                      </tr>
                      <tr>
                        <td>
                          <code>expiresAt</code>
                        </td>
                        <td>string</td>
                        <td>Session expiration (24 hours from creation)</td>
                      </tr>
                      <tr>
                        <td>
                          <code>importUrl</code>
                        </td>
                        <td>string</td>
                        <td>Redirect user to this URL</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Code Examples */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Code Examples</h2>
                <CodeTabs tabs={codeExamples} />
              </section>

              {/* Limits */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Limits</h2>
                <div className="api-docs-table-wrapper">
                  <table className="api-docs-table">
                    <thead>
                      <tr>
                        <th>Constraint</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Max request body</td>
                        <td>10 MB</td>
                      </tr>
                      <tr>
                        <td>Max cards per import</td>
                        <td>~50,000</td>
                      </tr>
                      <tr>
                        <td>Session expiration</td>
                        <td>24 hours</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Error Responses */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Error Responses</h2>
                <div className="api-docs-table-wrapper">
                  <table className="api-docs-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Error</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <span className="api-docs-status error">400</span>
                        </td>
                        <td>Invalid CSV format</td>
                        <td>CSV parsing failed</td>
                      </tr>
                      <tr>
                        <td>
                          <span className="api-docs-status error">400</span>
                        </td>
                        <td>Invalid base64</td>
                        <td>csvData is not valid base64</td>
                      </tr>
                      <tr>
                        <td>
                          <span className="api-docs-status error">400</span>
                        </td>
                        <td>Missing source</td>
                        <td>source field is required</td>
                      </tr>
                      <tr>
                        <td>
                          <span className="api-docs-status error">413</span>
                        </td>
                        <td>Payload too large</td>
                        <td>CSV exceeds 10MB limit</td>
                      </tr>
                      <tr>
                        <td>
                          <span className="api-docs-status error">500</span>
                        </td>
                        <td>Server error</td>
                        <td>Contact TradeMagic support</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* User Experience */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">User Experience</h2>
                <p className="api-docs-text">
                  After redirecting to <code>importUrl</code>, users will:
                </p>
                <ul className="api-docs-list">
                  <li>
                    <strong>Select condition</strong> - Choose default condition
                    for all cards (Near Mint, Lightly Played, etc.)
                  </li>
                  <li>
                    <strong>Preview cards</strong> - See card images and
                    quantities before importing
                  </li>
                  <li>
                    <strong>Sign in / Sign up</strong> - If not already logged
                    in
                  </li>
                  <li>
                    <strong>Import</strong> - Cards added to their TradeMagic
                    collection
                  </li>
                </ul>
              </section>

              {/* Testing */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Testing</h2>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">
                    <span className="api-docs-env-badge staging">Staging</span>
                    Staging Environment
                  </h3>
                  <p className="api-docs-text">
                    API:{' '}
                    <code>
                      https://staging-api.trademagic.gg/api/v1/import-session
                    </code>
                    <br />
                    Frontend:{' '}
                    <code>
                      https://staging.trademagic.gg/import/{'{your-source-id}'}?session=xxx
                    </code>
                  </p>
                </div>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">
                    <span className="api-docs-env-badge production">Prod</span>
                    Production Environment
                  </h3>
                  <p className="api-docs-text">
                    API:{' '}
                    <code>
                      https://api.trademagic.gg/api/v1/import-session
                    </code>
                    <br />
                    Frontend:{' '}
                    <code>
                      https://trademagic.gg/import/{'{your-source-id}'}?session=xxx
                    </code>
                  </p>
                </div>
              </section>

              {/* Support */}
              <div className="api-docs-support">
                <h2 className="api-docs-support-title">Need Help?</h2>
                <p className="api-docs-support-text">
                  Questions or issues with the integration?
                </p>
                <a
                  href="mailto:support@trademagic.gg"
                  className="api-docs-support-email"
                >
                  support@trademagic.gg
                </a>
              </div>
            </div>
          </div>
        </div>

        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername=""
        />

        <Footer />
      </div>
    </>
  );
};

export default ApiDocumentationPage;
